import { faArrowUpRightFromSquare, faCopy, faPause, faPlay } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  CircularProgress,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import get from 'lodash-es/get';
import React, { ReactNode, useEffect, useState } from 'react';
import { generatePath, useParams } from 'react-router-dom';

import { truncatedTextStyle } from '../../constants/contractConfigForms';
import { EDIT_AD_GROUP_URL } from '../../constants/routeUrls';
import { useContractConfigurationContext } from '../../hooks/useContractConfigurationContext';
import useModal from '../../hooks/useModal';
import usePauseToggleAdGroup from '../../hooks/usePauseToggleAdGroup';
import { useSnackBar } from '../../hooks/useSnackBar';
import { activeBackgroundColor, hoverBackgroundColor } from '../../styles/styles';
import DuplicateAdGroupForm from '../AdGroups/DuplicateAdGroupForm';

interface AdGroupNavProps {
  adGroup: any;
  onClick: () => void;
  index?: Number;
  count?: Number;
  isRefetching?: Boolean;
}

const connectedLine = (index, count, isPaused: boolean, complete: boolean): ReactNode => {
  const center = 20;

  let y1 = 0;
  if (index === 0) {
    y1 = center;
  }

  let y2 = 9999;
  if (index === count - 1) {
    y2 = center;
  }

  let dotRadius = '2.5';
  if (!complete || isPaused) {
    dotRadius = '4';
  }

  let dotColor = '#bbb';
  if (isPaused) {
    dotColor = '#FCAC5B';
  }
  if (!complete) {
    dotColor = '#ED4E4E';
  }

  return (
    <svg
      style={{
        position: 'absolute',
        top: 0,
        height: '100%',
        left: '33px',
        width: '8px',
      }}
    >
      {count >= 1 && <line x1="4" y1={y1} x2="4" y2={y2} stroke="#eee" strokeWidth="2" />}
      <circle cx="4" cy={center} r={dotRadius} fill={dotColor} />
    </svg>
  );
};

function AdGroupNav({ adGroup, index = 0, count = 0, onClick, isRefetching = false }: AdGroupNavProps) {
  const { id, name, paused: initialIsPaused, complete } = adGroup;
  const contractConfiguration = useContractConfigurationContext();

  const refetchContract = get(contractConfiguration, 'refetch');
  const onToggleAdGroupPauseCallback = get(contractConfiguration, 'onToggleAdGroupPauseCallback');
  const contractId = get(contractConfiguration, 'contractData.id');
  const [isHovered, setHovered] = useState(false);

  const [isPaused, setIsPaused] = useState(initialIsPaused);

  useEffect(() => {
    setIsPaused(initialIsPaused);
  }, [initialIsPaused]);

  const { showSnackBar } = useSnackBar();

  const [showDuplicateAdGroupModal, hideDuplicateAdGroupModal] = useModal(
    () => <DuplicateAdGroupForm adGroup={adGroup} onClose={hideDuplicateAdGroupModal} />,
    [adGroup]
  );

  const params = useParams();

  const pauseToggleMutation = usePauseToggleAdGroup(adGroup.id);

  const togglePause = () => {
    pauseToggleMutation.mutate(undefined, {
      onSuccess: (data) => {
        const newIsPaused = data?.model?.paused;
        setIsPaused(newIsPaused);
        if (refetchContract) {
          refetchContract();
        }
        if (onToggleAdGroupPauseCallback) {
          setTimeout(() => onToggleAdGroupPauseCallback(), 1000);
        }
        showSnackBar(`Ad group ${name} was successfully ${newIsPaused ? 'paused' : 'resumed'}.`, 'success', {
          event: newIsPaused ? 'ad_group_paused' : 'ad_group_resumed',
          ad_group_id: id,
          ad_group_name: name,
        });
      },
      onError: () => {
        showSnackBar(`Ad group ${name} failed to pause.`, 'error', {
          event: 'ad_group_pause_error',
          ad_group_id: id,
          ad_group_name: name,
        });
      },
    });
  };

  const openAdGroupInNewTab = () =>
    window.open(generatePath(EDIT_AD_GROUP_URL, { id: contractId.toString(), adGroupId: id.toString() }));

  let backgroundColor;
  if (isPaused) backgroundColor = '#FFF4DB';
  if (params.adGroupId === id?.toString()) backgroundColor = activeBackgroundColor;

  return (
    <Tooltip title={`${name} (${id})`} placement="right">
      <ListItem
        onClick={onClick}
        disablePadding
        sx={{
          backgroundColor,
          '&:hover': {
            backgroundColor: params.adGroupId === id?.toString() ? backgroundColor : hoverBackgroundColor,
          },
          cursor: 'pointer',
          px: 2,
        }}
        onMouseOver={() => setHovered(true)}
        onMouseOut={() => setHovered(false)}
      >
        <ListItemIcon sx={{ minWidth: 'auto', width: 2, pl: 2, paddingRight: '1px' }}>
          {connectedLine(index, count, isPaused, complete)}
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography className="side-bar-item-title">
              {name} ({id})
            </Typography>
          }
          inset
          primaryTypographyProps={{
            sx: truncatedTextStyle,
          }}
          sx={{ flexBasis: 0, pl: 6, py: 1, m: 0 }}
        />
        <Stack
          direction="row"
          spacing={1}
          ml={1}
          sx={{
            display: isHovered ? 'inherit' : 'none',
          }}
        >
          <Tooltip title={`${isPaused ? 'Resume' : 'Pause'} Ad Group`} placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                togglePause();
              }}
            >
              {!isRefetching && isPaused && <FontAwesomeIcon icon={faPlay} />}
              {!isRefetching && !isPaused && <FontAwesomeIcon icon={faPause} />}
              {isRefetching && <CircularProgress color="inherit" size={20} />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Duplicate Ad Group" placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                showDuplicateAdGroupModal();
              }}
            >
              <FontAwesomeIcon icon={faCopy} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Open in new tab" placement="top">
            <IconButton size="small" onClick={openAdGroupInNewTab}>
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
            </IconButton>
          </Tooltip>
        </Stack>
      </ListItem>
    </Tooltip>
  );
}

export default AdGroupNav;
